import { gql } from '@apollo/client';
import { format, isSameDay, parse, parseISO } from 'date-fns';
import styled from 'styled-components';
const gardensImage = 'https://BelvoirCastle.b-cdn.net/images/gardens.webp';

const retailImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/retail.webp';

const castleImage = 'https://BelvoirCastle.b-cdn.net/Castle/castle.webp';

const playgroundImage = 'https://BelvoirCastle.b-cdn.net/Adventure%20park/play.webp';

// Helper function to strip HTML tags
function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// Helper function to format ticket tier for display
function formatTicketTier(tier) {
  if (!tier) return '';
  
  // Convert tier to string if it's not already
  const tierStr = String(tier).trim();
  if (!tierStr) return '';
  
  const tierMap = {
    'super_early_bird': 'Super Early Bird',
    'early_bird': 'Early Bird',
    'advanced': 'Advanced',
    'standard': ''
  };
  
  return tierMap[tierStr.toLowerCase()] || '';
}

// Helper function to parse date string in various formats
function parseDateString(dateStr) {
  if (!dateStr) return null;
  
  try {
    // Try parsing ISO format first
    const isoDate = parseISO(dateStr);
    if (!isNaN(isoDate.getTime())) return isoDate;
    
    // Try parsing "Friday, May 2, 2025" format
    const matches = dateStr.match(/([A-Za-z]+), ([A-Za-z]+) (\d+), (\d{4})/);
    if (matches) {
      const [_, dayName, monthName, day, year] = matches;
      return parse(`${day} ${monthName} ${year}`, 'd MMMM yyyy', new Date());
    }
    
    // Try parsing "dd MMM yyyy" format
    const dateParts = dateStr.split(' ');
    if (dateParts.length >= 3) {
      const day = parseInt(dateParts[0], 10);
      const month = getMonthNumber(dateParts[1]);
      const year = parseInt(dateParts[2], 10);
      
      if (!isNaN(day) && month !== -1 && !isNaN(year)) {
        return new Date(year, month, day);
      }
    }
    
    // If all else fails, try native Date parsing
    const fallbackDate = new Date(dateStr);
    if (!isNaN(fallbackDate.getTime())) return fallbackDate;
    
  } catch (error) {

  }
  
  return null;
}

// Events that are part of the "Pirate Takeover" theme (May 2026)
function isPirateTakeoverEvent(title) {
  const t = (title || '').toLowerCase();
  return t.includes('circus workshops') || t.includes('pirate adventure') || t.includes('boom circus') || t.includes('pirates of the big top');
}

export function isPirateTakeoverByTitle(title) {
  return isPirateTakeoverEvent(title);
}

// Process WordPress events data into the format your components expect
export function processEvents(events, customImages = {}, limit = null) {

  if (!events) {

    return [];
  }
  
  // Use provided custom images or fall back to defaults
  const images = {
    castle: customImages.castle || castleImage,
    gardens: customImages.gardens || gardensImage,
    retail: customImages.retail || retailImage,
    playground: customImages.playground || playgroundImage
  };
  
  const processedEvents = events.map(event => {
    // Add debug logging to see what data we're getting

    // Determine the default image based on location
    let location = Array.isArray(event.eventDetails?.eventLocation) 
      ? event.eventDetails.eventLocation[0] 
      : event.eventDetails?.eventLocation;
    
    // Normalize location for comparison
    location = location?.toUpperCase() || '';

    const defaultImage = location === "CASTLE" ? images.castle :
                         location === "PARKLAND" || location === "GARDENS" ? images.gardens :
                         location === "RETAIL VILLAGE" || location.includes("RETAIL") ? images.retail :
                         images.playground;
    
    // Parse and format dates
    let startDate = null;
    let endDate = null;
    let formattedDate = '';
    
    // First try eventDetails dates
    if (event.eventDetails?.eventDate) {
      startDate = parseDateString(event.eventDetails.eventDate);
      endDate = event.eventDetails.eventEndDate 
        ? parseDateString(event.eventDetails.eventEndDate)
        : startDate;
    }
    
    // If no dates in eventDetails, try the main date field
    if (!startDate && event.date) {
      // Check if it's a date range (contains "to")
      if (event.date.includes(" to ")) {
        const [startStr, endStr] = event.date.split(" to ");
        startDate = parseDateString(startStr);
        endDate = parseDateString(endStr);
      } else {
        startDate = parseDateString(event.date);
        endDate = startDate;
      }
    }
    
    // Format the date string
    if (startDate && !isNaN(startDate.getTime())) {
      formattedDate = format(startDate, 'EEEE, MMMM d, yyyy');
      if (endDate && !isNaN(endDate.getTime()) && !isSameDay(startDate, endDate)) {
        formattedDate += ` to ${format(endDate, 'EEEE, MMMM d, yyyy')}`;
      }
    } else {
      formattedDate = event.date || '';
    }
    
    // Clean up description
    const description = stripHtml(event.eventDetails?.eventDescription || '');
    const shortDescription = stripHtml(event.eventDetails?.eventShortDescription || '');
    
    // Format ticket tier
    const ticketTier = formatTicketTier(event.eventDetails?.eventTicketTier);
    
    // Check if event is in the past using the end date
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    // Create end of day for the event's end date
    const eventEndDate = endDate ? new Date(endDate) : null;
    if (eventEndDate) {
      eventEndDate.setHours(23, 59, 59, 999); // Set to end of day
    }
    
    const isPastEvent = eventEndDate ? eventEndDate < now : startDate && startDate < now;

    // Only include the event if it's not in the past
    if (!isPastEvent) {
      return {
        id: event.id,
        title: event.title,
        date: formattedDate,
        description: shortDescription || description || '',
        location: location,
        image: event.featuredImage?.node?.sourceUrl || defaultImage,
        ticketTier: ticketTier,
        isPirateTakeover: isPirateTakeoverEvent(event.title),
        eventDetails: {
          ...event.eventDetails,
          fullDescription: description
        },
        normalizedStartDate: startDate,
        normalizedEndDate: endDate,
        eventTags: event.eventDetails?.eventTags || []
      };
    }
    
    return null;
  }).filter(Boolean); // Remove null entries

  // Sort events by date
  const sortedEvents = processedEvents.sort((a, b) => {
    if (!a.normalizedStartDate) return 1;
    if (!b.normalizedStartDate) return -1;
    return a.normalizedStartDate - b.normalizedStartDate;
  });
  
  return limit ? sortedEvents.slice(0, limit) : sortedEvents;
}

// Helper function to convert month abbreviation to number (0-11)
function getMonthNumber(monthAbbr) {
  if (!monthAbbr) return -1;
  
  const months = {
    'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
    'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11,
    'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
    'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
  };
  
  // Handle case insensitivity
  const normalizedMonth = monthAbbr.charAt(0).toUpperCase() + monthAbbr.slice(1).toLowerCase();
  return months[normalizedMonth] !== undefined ? months[normalizedMonth] : -1;
}

// GraphQL query for events - UPDATED to match actual schema field names
export const GET_EVENTS_QUERY = gql`
  query GetEvents {
    events(first: 100) {
      nodes {
        id
        title
        date
        featuredImage {
          node {
            sourceUrl
          }
        }
        eventDetails {
          eventLocation
          eventDescription
          eventShortDescription
          eventDate
          eventEndDate
          eventTicketTier
          eventTags
          eventIsFeatured
        }
      }
    }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Tag = styled.span`
  background-color: #f0f0f0;
  color: #333;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 12px;
  text-transform: capitalize;
`; 

// Function to filter out past hardcoded events
export function filterPastHardcodedEvents(hardcodedEvents) {
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Set to start of day for comparison
  
  return hardcodedEvents.filter(event => {
    const eventDetails = event.eventDetails || {};
    let eventEndDate = null;
    let eventStartDate = null;
    
    // Parse end date if available
    if (eventDetails.eventEndDate) {
      eventEndDate = parseDateString(eventDetails.eventEndDate);
      if (eventEndDate) {
        eventEndDate.setHours(23, 59, 59, 999); // Set to end of day
      }
    }
    
    // Parse start date if available
    if (eventDetails.eventDate) {
      eventStartDate = parseDateString(eventDetails.eventDate);
    }
    
    // If we have an end date, check if it's in the past
    if (eventEndDate) {
      return eventEndDate >= now;
    }
    
    // If no end date but we have a start date, check if it's in the past
    if (eventStartDate) {
      eventStartDate.setHours(23, 59, 59, 999); // Set to end of day for single-day events
      return eventStartDate >= now;
    }
    
    // If no date information, keep the event (shouldn't happen, but safe fallback)
    return true;
  });
}

// Function to combine hardcoded events with WordPress events and sort by date
export function combineAndSortEvents(hardcodedEvents, wordpressEvents, customImages = {}) {
  // Filter out past hardcoded events first
  const filteredHardcodedEvents = filterPastHardcodedEvents(hardcodedEvents);
  
  // Process WordPress events first
  const processedWordPressEvents = processEvents(wordpressEvents, customImages);
  
  // Process hardcoded events to match the same format
  const processedHardcodedEvents = filteredHardcodedEvents.map(event => {
    // Parse the date string to get a proper Date object for sorting
    let normalizedStartDate = null;
    let normalizedEndDate = null;
    
    if (event.eventDetails?.eventDate) {
      normalizedStartDate = parseDateString(event.eventDetails.eventDate);
    }
    
    if (event.eventDetails?.eventEndDate) {
      normalizedEndDate = parseDateString(event.eventDetails.eventEndDate);
    }
    
    // If we have a date string in the event.date field, try to parse it
    if (!normalizedStartDate && event.date) {
      // Try to extract start date from date range strings like "Saturday, November 29, 2025 to Wednesday, December 31, 2025"
      const dateMatch = event.date.match(/([A-Za-z]+), ([A-Za-z]+) (\d+), (\d{4})/);
      if (dateMatch) {
        const [_, dayName, monthName, day, year] = dateMatch;
        normalizedStartDate = parse(`${day} ${monthName} ${year}`, 'd MMMM yyyy', new Date());
      }
    }
    
    return {
      ...event,
      normalizedStartDate,
      normalizedEndDate,
      isPirateTakeover: event.isPirateTakeover ?? isPirateTakeoverEvent(event.title)
    };
  });
  
  // Combine both arrays
  const allEvents = [...processedHardcodedEvents, ...processedWordPressEvents];
  
  // Sort by start date
  const sortedEvents = allEvents.sort((a, b) => {
    if (!a.normalizedStartDate) return 1;
    if (!b.normalizedStartDate) return -1;
    return a.normalizedStartDate - b.normalizedStartDate;
  });
  
  return sortedEvents;
}